import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Calendar, Scissors, Settings as SettingsIcon, ListChecks, CalendarDays, ChevronLeft, ChevronRight, X, DollarSign, Users, LayoutGrid, Bell, Clock, TrendingUp } from "lucide-react";
import { startOfMonth, endOfMonth, eachDayOfInterval, format, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
  }, [admin, navigate]);

  if (admin === null || admin === false) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#C5B382]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#222] px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="font-serif tracking-[0.12em]">SLAVIC · Studio Admin</div>
        <div className="flex items-center gap-4">
          <NotificationBell onView={() => setTab("bookings")} />
          <button
            data-testid="admin-logout"
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="flex items-center gap-2 text-neutral-400 hover:text-[#C5B382] text-sm transition-colors"
          >
            <LogOut size={16} /> <span className="hidden sm:inline">Log out</span>
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-2 mb-8 border-b border-[#222] overflow-x-auto">
          {[
            { id: "overview", label: "Overview", icon: LayoutGrid },
            { id: "bookings", label: "Bookings", icon: Calendar },
            { id: "calendar", label: "Calendar", icon: CalendarDays },
            { id: "clients", label: "Clients", icon: Users },
            { id: "waitlist", label: "Waitlist", icon: ListChecks },
            { id: "payments", label: "Payments", icon: DollarSign },
            { id: "services", label: "Services", icon: Scissors },
            { id: "settings", label: "Availability", icon: SettingsIcon },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === t.id ? "border-[#C5B382] text-white" : "border-transparent text-neutral-500 hover:text-neutral-300"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab onGoto={setTab} />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "clients" && <ClientsTab />}
        {tab === "waitlist" && <WaitlistTab />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

function NotificationBell({ onView }) {
  const [data, setData] = useState({ count: 0, items: [] });
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.count > 0) {
      await api.post("/admin/notifications/seen").catch(() => {});
      // keep list visible but clear the badge
      setData((d) => ({ ...d, count: 0 }));
    }
  };

  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={toggle}
        className="relative text-neutral-400 hover:text-[#C5B382] transition-colors p-1">
        <Bell size={20} />
        {data.count > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 bg-[#C5B382] text-black text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
            {data.count}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-[#141414] border border-[#333] rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#222] flex items-center justify-between">
            <span className="text-sm font-medium">New bookings</span>
            <button onClick={() => setOpen(false)} className="text-neutral-500 hover:text-white"><X size={15} /></button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.items.length === 0 ? (
              <p className="text-neutral-500 text-sm px-4 py-6 text-center">No new bookings right now.</p>
            ) : data.items.map((b) => (
              <button key={b.id} data-testid={`notif-item-${b.id}`}
                onClick={() => { setOpen(false); onView && onView(); }}
                className="w-full text-left px-4 py-3 border-b border-[#1c1c1c] hover:bg-[#1a1a1a] transition-colors">
                <div className="text-sm text-white">{b.client_name}</div>
                <div className="text-xs text-neutral-400">{b.service_name}</div>
                <div className="text-xs text-[#C5B382] mt-0.5">{b.date_display} · {b.time}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewTab({ onGoto }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader2 className="animate-spin text-[#C5B382]" />;

  const cards = [
    { label: "Today's appointments", value: data.stats.today_count, icon: Clock },
    { label: "This week", value: data.stats.week_count, icon: CalendarDays },
    { label: "Total bookings", value: data.stats.total_bookings, icon: Calendar },
    { label: "Total revenue", value: `$${data.stats.total_revenue.toFixed(0)}`, icon: TrendingUp },
  ];

  const ApptRow = ({ b }) => (
    <div data-testid={`overview-appt-${b.id}`} className="flex items-center justify-between border border-[#222] bg-[#111] rounded-xl px-4 py-3">
      <div className="min-w-0">
        <div className="text-sm text-white truncate">{b.client_name} <span className="text-neutral-500">· {b.service_name}</span></div>
        <div className="text-xs text-neutral-400 mt-0.5">{b.client_phone}</div>
      </div>
      <div className="text-right shrink-0 ml-3">
        <div className="text-[#C5B382] text-sm font-medium">{b.time}</div>
        <div className="text-[11px] text-neutral-500">{b.remaining > 0 ? `$${b.remaining.toFixed(0)} due` : "Paid"}</div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-overview" className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label}`} className="border border-[#222] bg-[#111] rounded-2xl p-4">
            <div className="flex items-center gap-2 text-neutral-500 text-[11px] uppercase tracking-wider">
              <c.icon size={14} className="text-[#C5B382]" /> {c.label}
            </div>
            <div className="text-2xl sm:text-3xl font-serif mt-2 text-white">{c.value}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl">Today's schedule</h3>
          <span className="text-xs text-neutral-500">{format(new Date(data.today + "T00:00:00"), "EEEE, d MMMM")}</span>
        </div>
        <div className="space-y-2">
          {data.today_appts.length === 0
            ? <p className="text-neutral-500 text-sm border border-[#222] bg-[#111] rounded-xl px-4 py-6 text-center">No appointments today. Enjoy your day off ✨</p>
            : data.today_appts.map((b) => <ApptRow key={b.id} b={b} />)}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl">Upcoming</h3>
          <button data-testid="overview-view-all" onClick={() => onGoto("bookings")} className="text-xs text-[#C5B382] hover:text-white transition-colors">View all bookings →</button>
        </div>
        <div className="space-y-2">
          {data.upcoming.length === 0
            ? <p className="text-neutral-500 text-sm border border-[#222] bg-[#111] rounded-xl px-4 py-6 text-center">No upcoming appointments yet.</p>
            : data.upcoming.map((b) => (
              <div key={b.id} data-testid={`overview-upcoming-${b.id}`} className="flex items-center justify-between border border-[#222] bg-[#111] rounded-xl px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{b.client_name} <span className="text-neutral-500">· {b.service_name}</span></div>
                  <div className="text-xs text-neutral-400 mt-0.5">{b.date_display}</div>
                </div>
                <div className="text-[#C5B382] text-sm font-medium shrink-0 ml-3">{b.time}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState(null);
  const [editDay, setEditDay] = useState(null);
  const [editBooking, setEditBooking] = useState(null);
  const [services, setServices] = useState([]);

  const load = () => api.get("/admin/bookings").then((r) => setBookings(r.data));
  useEffect(() => {
    load();
    api.get("/services").then((r) => {
      const flat = [];
      r.data.forEach((g) => g.items.forEach((it) => flat.push({ id: it.id, label: it.type === "Single" ? it.group : `${it.group} — ${it.type}`, price: it.price })));
      setServices(flat);
    });
  }, []);

  const cancel = async (id) => {
    await api.patch(`/admin/bookings/${id}/cancel`);
    load();
  };

  const noShow = async (id) => {
    await api.post(`/admin/bookings/${id}/no-show`);
    load();
  };

  if (!bookings) return <Loader2 className="animate-spin text-[#C5B382]" />;

  const badge = (s) =>
    s === "confirmed" ? "text-green-400 border-green-400/40"
    : s === "cancelled" ? "text-red-400 border-red-400/40"
    : s === "no_show" ? "text-orange-400 border-orange-400/40"
    : "text-yellow-400 border-yellow-400/40";

  const today = new Date().toISOString().slice(0, 10);
  const sorted = [...bookings].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
  const days = [];
  const byDay = {};
  sorted.forEach((b) => {
    if (!byDay[b.date]) { byDay[b.date] = { label: b.date_display, items: [] }; days.push(b.date); }
    byDay[b.date].items.push(b);
  });

  return (
    <div data-testid="admin-bookings" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3 border border-[#222] bg-[#111] rounded-xl px-4 py-3">
        <span className="text-sm text-neutral-400">Starting your day? Add a break or adjust today's hours.</span>
        <button data-testid="edit-today" onClick={() => setEditDay(today)} className="text-xs bg-[#C5B382] hover:bg-[#B8A572] text-black font-semibold uppercase tracking-wider px-4 py-2 rounded-full transition-colors">Today's hours / breaks</button>
      </div>
      {days.length === 0 && (
        <p className="text-neutral-500">No appointments yet. New bookings will appear here as a day-by-day calendar.</p>
      )}
      {days.map((d) => (
        <div key={d}>
          <div className="flex items-center gap-3 mb-3">
            <span className={`text-sm font-medium ${d >= today ? "text-[#C5B382]" : "text-neutral-500"}`}>{byDay[d].label}</span>
            <span className="text-xs text-neutral-600">{byDay[d].items.length} appt{byDay[d].items.length > 1 ? "s" : ""}</span>
            <button data-testid={`edit-day-${d}`} onClick={() => setEditDay(d)} className="text-xs text-neutral-500 hover:text-[#C5B382] transition-colors">+ Break / hours</button>
            <span className="flex-1 h-px bg-[#222]" />
          </div>
          <div className="space-y-3">
            {byDay[d].items.map((b) => (
              <div key={b.id} className="border border-[#222] bg-[#111] p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-medium"><span className="text-[#C5B382]">{b.time}</span> · {b.client_name} · {b.service_name}</div>
                  <div className="text-sm text-neutral-500">{b.client_email} · {b.client_phone}</div>
                  {b.notes && <div className="text-xs text-neutral-600 mt-1">Note: {b.notes}</div>}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right text-sm">
                    <div>${b.amount_paid.toFixed(2)} paid</div>
                    {b.remaining > 0 && <div className="text-neutral-500 text-xs">${b.remaining.toFixed(2)} due</div>}
                  </div>
                  <span className={`text-xs uppercase tracking-wider border px-2.5 py-1 ${badge(b.status)}`}>
                    {b.status.replace("_", " ")}
                  </span>
                  {b.retainer_forfeited && (
                    <span className="text-[10px] uppercase tracking-wider text-orange-400/80">retainer kept</span>
                  )}
                  {b.status !== "cancelled" && b.status !== "no_show" && (
                    <button
                      data-testid={`edit-booking-${b.id}`}
                      onClick={() => setEditBooking(b)}
                      className="text-xs text-neutral-500 hover:text-[#C5B382] transition-colors"
                    >
                      Edit
                    </button>
                  )}
                  {b.status !== "cancelled" && b.status !== "no_show" && (
                    <button
                      data-testid={`no-show-booking-${b.id}`}
                      onClick={() => noShow(b.id)}
                      className="text-xs text-neutral-500 hover:text-orange-400 transition-colors"
                    >
                      No-show
                    </button>
                  )}
                  {b.status !== "cancelled" && b.status !== "no_show" && (
                    <button
                      data-testid={`cancel-booking-${b.id}`}
                      onClick={() => cancel(b.id)}
                      className="text-xs text-neutral-500 hover:text-red-400 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              {b.status !== "cancelled" && b.status !== "no_show" && b.remaining > 0 && (
                <BalanceCollector booking={b} onDone={load} />
              )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {editDay && (
        <DayEditorLoader date={editDay} appts={(byDay[editDay]?.items) || []} onClose={() => setEditDay(null)} onSaved={load} />
      )}
      {editBooking && (
        <BookingEditor booking={editBooking} services={services} onClose={() => setEditBooking(null)} onSaved={load} />
      )}
    </div>
  );
}

function BookingEditor({ booking, services, onClose, onSaved }) {
  const [serviceId, setServiceId] = useState(booking.service_id);
  const [date, setDate] = useState(booking.date);
  const [time, setTime] = useState(booking.time);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.patch(`/admin/bookings/${booking.id}`, { service_id: serviceId, date, time });
      await onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not save. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="booking-editor" onClick={(e) => e.stopPropagation()} className="bg-[#111] border border-[#333] rounded-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl">Edit appointment</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-sm text-neutral-500">{booking.client_name} · {booking.client_email}</p>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1.5">Service</label>
          <select data-testid="edit-service" value={serviceId} onChange={(e) => setServiceId(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#333] px-3 py-2.5 text-sm rounded text-white focus:border-[#C5B382] outline-none">
            {services.map((s) => <option key={s.id} value={s.id}>{s.label} — ${s.price.toFixed(0)}</option>)}
          </select>
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1.5">Date</label>
            <input data-testid="edit-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#333] px-3 py-2.5 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
          </div>
          <div className="w-32">
            <label className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1.5">Time</label>
            <input data-testid="edit-time" type="time" value={time} onChange={(e) => setTime(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#333] px-3 py-2.5 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
          </div>
        </div>
        <p className="text-xs text-neutral-600">Changing the service updates the price and recalculates the balance due. Please let the client know if you move their time.</p>
        {err && <p className="text-red-400 text-sm">{err}</p>}
        <div className="flex justify-end pt-1">
          <button data-testid="edit-save" onClick={save} disabled={saving}
            className="bg-[#C5B382] hover:bg-[#B8A572] text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider px-6 py-2.5 rounded-full transition-colors">
            {saving ? "…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BalanceCollector({ booking, onDone }) {
  const [amount, setAmount] = useState(String(booking.remaining.toFixed(2)));
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);

  const record = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/collect`, { method, amount: Number(amount) });
      await onDone();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not record the payment.");
    } finally { setBusy(false); }
  };

  const sendLink = async () => {
    try {
      await api.post(`/admin/bookings/${booking.id}/send-balance-link`);
      alert("A secure payment link for the remaining balance has been emailed to the client.");
      await onDone();
    } catch {
      alert("Could not send the payment link.");
    }
  };

  const inp = "bg-[#0A0A0A] border border-[#333] px-2 py-1 text-xs rounded text-white focus:border-[#C5B382] outline-none";
  return (
    <div className="w-full flex flex-wrap items-center gap-2 mt-3 border-t border-[#222] pt-3">
      <span className="text-xs text-neutral-500">${booking.remaining.toFixed(2)} due — record part or full:</span>
      <input data-testid={`collect-amount-${booking.id}`} type="number" min="0" step="1" max={booking.remaining} value={amount} onChange={(e) => setAmount(e.target.value)} className={`w-20 ${inp}`} />
      <select data-testid={`collect-method-${booking.id}`} value={method} onChange={(e) => setMethod(e.target.value)} className={inp}>
        <option value="cash">Cash</option>
        <option value="transfer">Transfer</option>
        <option value="card">Card in person</option>
      </select>
      <button data-testid={`collect-record-${booking.id}`} disabled={busy} onClick={record}
        className="text-xs border border-[#C5B382]/40 text-[#C5B382] px-3 py-1 rounded-full hover:bg-[#C5B382] hover:text-black transition-colors">
        {busy ? "…" : "Record"}
      </button>
      <button data-testid={`send-link-${booking.id}`} onClick={sendLink}
        className="text-xs bg-[#C5B382] text-black px-2.5 py-1 rounded-full hover:bg-white transition-colors">
        Send pay link for rest
      </button>
    </div>
  );
}

function DayEditorLoader({ date, appts, onClose, onSaved }) {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)); }, []);
  if (!settings) return null;
  const py = (new Date(date + "T00:00:00").getDay() + 6) % 7;
  const override = (settings.special_dates || {})[date] || null;
  const weekly = (settings.hours || {})[String(py)] || null;
  return <DayEditor date={date} override={override} weekly={weekly} appts={appts} onClose={onClose} onSaved={onSaved} />;
}

function DayEditor({ date, override, weekly, appts, onClose, onSaved }) {
  const weeklyClosed = !weekly || weekly.closed;
  const [closed, setClosed] = useState(!!(override && override.closed));
  const [customHours, setCustomHours] = useState(override ? !!override.open : weeklyClosed);
  const [openT, setOpenT] = useState(override?.open || weekly?.open || "09:00");
  const [closeT, setCloseT] = useState(override?.close || weekly?.close || "17:00");
  const [breaks, setBreaks] = useState(override?.breaks ? override.breaks.map((b) => ({ ...b })) : []);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const setBreak = (i, k, v) => setBreaks((arr) => arr.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const addBreak = () => setBreaks((arr) => [...arr, { start: "12:00", end: "13:00" }]);
  const removeBreak = (i) => setBreaks((arr) => arr.filter((_, j) => j !== i));

  const submit = async (action) => {
    setSaving(true); setErr("");
    try {
      const body = action === "clear"
        ? { date, action: "clear" }
        : { date, action: "set", closed, custom_hours: !closed && customHours, open: openT, close: closeT, breaks: closed ? [] : breaks };
      await api.put("/admin/day-override", body);
      await onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not save. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="day-editor" onClick={(e) => e.stopPropagation()} className="bg-[#111] border border-[#333] rounded-2xl w-full max-w-md p-6 space-y-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl">{format(new Date(date + "T00:00:00"), "EEE, d MMM yyyy")}</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Appointments ({appts.length})</div>
          {appts.length ? (
            <div className="space-y-1 max-h-28 overflow-y-auto">
              {appts.map((a) => (
                <div key={a.id} className="text-sm flex justify-between gap-3">
                  <span className="text-neutral-300">{a.time} · {a.client_name}</span>
                  <span className="text-neutral-500 truncate">{a.service_name}</span>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-neutral-600">No appointments booked.</div>}
        </div>

        <div className="space-y-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500">Availability for this day</div>

          <button data-testid="day-toggle-closed" onClick={() => setClosed((v) => !v)}
            className={`w-full text-left border rounded-xl px-4 py-3 text-sm transition-colors ${closed ? "border-red-400 bg-red-400/10 text-red-300" : "border-[#333] hover:border-[#555]"}`}>
            {closed ? "✓ " : ""}Closed — day off (no availability)
          </button>

          {!closed && (
            <div className="space-y-3">
              {weeklyClosed && (
                <p className="text-xs text-neutral-500">This weekday is normally closed — choose custom hours to open it for this date.</p>
              )}
              <div className="flex gap-2">
                <button data-testid="day-hours-weekly" disabled={weeklyClosed} onClick={() => setCustomHours(false)}
                  className={`flex-1 border rounded-xl px-3 py-2.5 text-sm transition-colors disabled:opacity-30 ${!customHours ? "border-[#C5B382] bg-[#C5B382]/10" : "border-[#333] hover:border-[#555]"}`}>
                  Weekly hours{weekly && !weekly.closed ? ` (${weekly.open}–${weekly.close})` : ""}
                </button>
                <button data-testid="day-hours-custom" onClick={() => setCustomHours(true)}
                  className={`flex-1 border rounded-xl px-3 py-2.5 text-sm transition-colors ${customHours ? "border-[#C5B382] bg-[#C5B382]/10" : "border-[#333] hover:border-[#555]"}`}>
                  Custom hours
                </button>
              </div>
              {customHours && (
                <div className="flex items-center gap-2">
                  <input data-testid="day-open" type="time" value={openT} onChange={(e) => setOpenT(e.target.value)} className="bg-[#0A0A0A] border border-[#333] px-2 py-1.5 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
                  <span className="text-neutral-600">–</span>
                  <input data-testid="day-close" type="time" value={closeT} onChange={(e) => setCloseT(e.target.value)} className="bg-[#0A0A0A] border border-[#333] px-2 py-1.5 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
                </div>
              )}

              <div className="border-t border-[#222] pt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-neutral-500">Breaks (blocked times)</span>
                  <button data-testid="day-add-break" onClick={addBreak} className="text-xs text-[#C5B382] hover:text-white transition-colors">+ Add a break</button>
                </div>
                {breaks.length === 0 ? (
                  <p className="text-xs text-neutral-600">No breaks. Add one to block a lunch break or personal time.</p>
                ) : (
                  <div className="space-y-2">
                    {breaks.map((b, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`day-break-${i}`}>
                        <input data-testid={`break-start-${i}`} type="time" value={b.start} onChange={(e) => setBreak(i, "start", e.target.value)} className="bg-[#0A0A0A] border border-[#333] px-2 py-1.5 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
                        <span className="text-neutral-600">–</span>
                        <input data-testid={`break-end-${i}`} type="time" value={b.end} onChange={(e) => setBreak(i, "end", e.target.value)} className="bg-[#0A0A0A] border border-[#333] px-2 py-1.5 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
                        <button data-testid={`break-remove-${i}`} onClick={() => removeBreak(i)} className="text-neutral-500 hover:text-red-400 ml-auto"><X size={15} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {err && <p className="text-red-400 text-sm">{err}</p>}

        <div className="flex items-center justify-between pt-1">
          <button data-testid="day-mode-reset" onClick={() => submit("clear")} disabled={saving} className="text-xs text-neutral-500 hover:text-white transition-colors">Reset to weekly hours</button>
          <button data-testid="day-save" onClick={() => submit("set")} disabled={saving} className="bg-[#C5B382] hover:bg-[#B8A572] text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider px-6 py-2.5 rounded-full transition-colors">
            {saving ? "…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CalendarTab() {
  const [month, setMonth] = useState(new Date());
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [rStart, setRStart] = useState("");
  const [rEnd, setREnd] = useState("");
  const [rBusy, setRBusy] = useState(false);

  const load = async () => {
    const [b, s] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/settings")]);
    setBookings(b.data); setSettings(s.data);
  };
  useEffect(() => { load(); }, []);

  const applyRange = async (mode) => {
    if (!rStart || !rEnd) return;
    setRBusy(true);
    try {
      await api.put("/admin/day-override-range", { start: rStart, end: rEnd, mode });
      await load();
      setRangeOpen(false); setRStart(""); setREnd("");
    } finally { setRBusy(false); }
  };

  if (!settings) return <Loader2 className="animate-spin text-[#C5B382]" />;

  const special = settings.special_dates || {};
  const blocked = new Set(settings.blocked_dates || []);
  const hours = settings.hours || {};
  const apptsByDate = {};
  bookings.filter((b) => b.status !== "cancelled" && b.status !== "no_show").forEach((b) => {
    (apptsByDate[b.date] = apptsByDate[b.date] || []).push(b);
  });
  Object.values(apptsByDate).forEach((l) => l.sort((a, b) => a.time.localeCompare(b.time)));

  const statusFor = (ds, day) => {
    const py = (day.getDay() + 6) % 7;
    const ov = special[ds] || null;
    const hasBreak = !!(ov && ov.breaks && ov.breaks.length);
    if (blocked.has(ds) || (ov && ov.closed)) return { type: "closed", label: "Closed", hasBreak: false, ov, py };
    if (ov && ov.open) return { type: "custom", label: `${ov.open}–${ov.close}`, hasBreak, ov, py };
    const cfg = hours[String(py)];
    if (!cfg || cfg.closed) return { type: "weekly-closed", label: "Closed", hasBreak: false, ov, py };
    return { type: "open", label: `${cfg.open}–${cfg.close}`, hasBreak, ov, py };
  };

  const first = startOfMonth(month);
  const days = eachDayOfInterval({ start: first, end: endOfMonth(month) });
  const lead = first.getDay();
  const selDate = selected ? new Date(selected + "T00:00:00") : null;
  const weekStart = startOfWeek(cursor, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(cursor, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const goPrev = () => view === "month" ? setMonth(subMonths(month, 1)) : view === "week" ? setCursor(subWeeks(cursor, 1)) : setCursor(addDays(cursor, -1));
  const goNext = () => view === "month" ? setMonth(addMonths(month, 1)) : view === "week" ? setCursor(addWeeks(cursor, 1)) : setCursor(addDays(cursor, 1));
  const goToday = () => { setMonth(new Date()); setCursor(new Date()); };
  const title = view === "month" ? format(month, "MMMM yyyy")
    : view === "week" ? `${format(weekStart, "d MMM")} – ${format(weekEnd, "d MMM yyyy")}`
    : format(cursor, "EEEE, d MMMM yyyy");

  return (
    <div data-testid="admin-calendar">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h2 className="font-serif text-2xl">{title}</h2>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-[#111] border border-[#333] rounded-lg p-0.5">
            {["day", "week", "month"].map((v) => (
              <button key={v} data-testid={`calendar-view-${v}`} onClick={() => setView(v)}
                className={`text-xs px-3 py-1.5 rounded-md capitalize transition-colors ${view === v ? "bg-[#C5B382] text-black font-semibold" : "text-neutral-400 hover:text-white"}`}>
                {v}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="calendar-prev" onClick={goPrev} className="p-2 border border-[#333] rounded-lg hover:border-[#C5B382] transition-colors"><ChevronLeft size={16} /></button>
            <button data-testid="calendar-today" onClick={goToday} className="px-3 py-2 border border-[#333] rounded-lg text-xs hover:border-[#C5B382] transition-colors">Today</button>
            <button data-testid="calendar-next" onClick={goNext} className="p-2 border border-[#333] rounded-lg hover:border-[#C5B382] transition-colors"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          <button data-testid="new-appointment-btn" onClick={() => setNewOpen(true)}
            className="text-xs bg-[#C5B382] hover:bg-[#B8A572] text-black font-semibold uppercase tracking-wider rounded-lg px-3 py-2 transition-colors">
            + New appointment
          </button>
          <button data-testid="range-toggle" onClick={() => setRangeOpen((v) => !v)}
            className="text-xs border border-[#333] rounded-lg px-3 py-2 hover:border-[#C5B382] transition-colors">
            Block time off (holiday)
          </button>
        </div>
        {rangeOpen && (
          <div data-testid="range-panel" className="mt-3 flex flex-wrap items-center gap-2 border border-[#222] bg-[#111] rounded-xl p-3">
            <input data-testid="range-start" type="date" value={rStart} onChange={(e) => setRStart(e.target.value)} className="bg-[#0A0A0A] border border-[#333] px-2 py-1.5 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
            <span className="text-neutral-600">→</span>
            <input data-testid="range-end" type="date" value={rEnd} onChange={(e) => setREnd(e.target.value)} className="bg-[#0A0A0A] border border-[#333] px-2 py-1.5 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
            <button data-testid="range-block" onClick={() => applyRange("closed")} disabled={rBusy || !rStart || !rEnd}
              className="bg-[#C5B382] hover:bg-[#B8A572] disabled:opacity-30 text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider px-4 py-2 rounded-full transition-colors">
              {rBusy ? "…" : "Block dates"}
            </button>
            <button data-testid="range-clear" onClick={() => applyRange("clear")} disabled={rBusy || !rStart || !rEnd}
              className="text-xs text-neutral-400 hover:text-white transition-colors">Clear dates</button>
          </div>
        )}
      </div>

      {view === "month" && (
        <>
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="text-center text-[10px] uppercase tracking-wider text-neutral-600 pb-2">{d}</div>
            ))}
            {Array.from({ length: lead }).map((_, i) => <div key={"b" + i} />)}
            {days.map((day) => {
              const ds = format(day, "yyyy-MM-dd");
              const st = statusFor(ds, day);
              const cnt = (apptsByDate[ds] || []).length;
              const closed = st.type === "closed" || st.type === "weekly-closed";
              return (
                <button key={ds} data-testid={`calendar-day-${ds}`} onClick={() => setSelected(ds)}
                  className={`aspect-square sm:aspect-[4/3] border rounded-lg p-1.5 sm:p-2 text-left flex flex-col transition-colors ${closed ? "border-[#222] bg-[#0E0E0E]" : "border-[#2a2a2a] bg-[#141414] hover:border-[#C5B382]"}`}>
                  <span className={`text-sm ${closed ? "text-neutral-600" : "text-white"}`}>{day.getDate()}</span>
                  <span className={`text-[9px] mt-0.5 truncate ${st.type === "custom" ? "text-[#C5B382]" : closed ? "text-neutral-700" : "text-neutral-500"}`}>{st.label}{st.hasBreak ? " · break" : ""}</span>
                  <span className="mt-auto">{cnt > 0 && <span className="inline-block text-[10px] font-semibold text-[#C5B382] bg-[#C5B382]/15 rounded-full px-1.5">{cnt} appt{cnt > 1 ? "s" : ""}</span>}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-neutral-600 mt-4">Tap any day to set custom hours, mark it as a day off, or reset to your weekly hours. Champagne text = special hours for that date.</p>
        </>
      )}

      {view === "week" && (
        <div data-testid="calendar-week" className="grid grid-cols-1 sm:grid-cols-7 gap-2">
          {weekDays.map((day) => {
            const ds = format(day, "yyyy-MM-dd");
            const st = statusFor(ds, day);
            const list = apptsByDate[ds] || [];
            const closed = st.type === "closed" || st.type === "weekly-closed";
            return (
              <div key={ds} data-testid={`week-day-${ds}`} className={`border rounded-xl p-2.5 flex flex-col ${closed ? "border-[#222] bg-[#0E0E0E]" : "border-[#2a2a2a] bg-[#141414]"}`}>
                <button onClick={() => setSelected(ds)} className="text-left mb-2 hover:opacity-80 transition-opacity">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">{format(day, "EEE")}</div>
                  <div className={`text-sm ${closed ? "text-neutral-600" : "text-white"}`}>{format(day, "d MMM")}</div>
                  <div className={`text-[10px] ${st.type === "custom" ? "text-[#C5B382]" : "text-neutral-500"}`}>{st.label}{st.hasBreak ? " · break" : ""}</div>
                </button>
                <div className="space-y-1">
                  {list.length === 0 ? <p className="text-[11px] text-neutral-700">—</p>
                    : list.map((b) => (
                      <button key={b.id} data-testid={`week-appt-${b.id}`} onClick={() => setSelected(ds)}
                        className="w-full text-left text-[11px] bg-[#C5B382]/10 border border-[#C5B382]/20 rounded px-1.5 py-1 hover:bg-[#C5B382]/20 transition-colors">
                        <span className="text-[#C5B382] font-medium">{b.time}</span> <span className="text-neutral-300">{b.client_name}</span>
                      </button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "day" && (() => {
        const ds = format(cursor, "yyyy-MM-dd");
        const st = statusFor(ds, cursor);
        const list = apptsByDate[ds] || [];
        const closed = st.type === "closed" || st.type === "weekly-closed";
        return (
          <div data-testid="calendar-day-view" className="space-y-3">
            <div className="flex items-center justify-between border border-[#222] bg-[#111] rounded-xl px-4 py-3">
              <span className="text-sm text-neutral-400">{closed ? "Closed" : `Open ${st.label}`}{st.hasBreak ? " · break" : ""}</span>
              <button onClick={() => setSelected(ds)} className="text-xs text-[#C5B382] hover:text-white transition-colors">Edit hours / breaks</button>
            </div>
            {list.length === 0
              ? <p className="text-neutral-500 text-sm border border-[#222] bg-[#111] rounded-xl px-4 py-8 text-center">No appointments this day.</p>
              : list.map((b) => (
                <div key={b.id} data-testid={`day-appt-${b.id}`} className="flex items-center justify-between border border-[#222] bg-[#111] rounded-xl px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{b.client_name} <span className="text-neutral-500">· {b.service_name}</span></div>
                    <div className="text-xs text-neutral-400 mt-0.5">{b.client_phone} · {b.client_email}</div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-[#C5B382] font-medium">{b.time}</div>
                    <div className="text-[11px] text-neutral-500">{b.remaining > 0 ? `$${b.remaining.toFixed(0)} due` : "Paid"}</div>
                  </div>
                </div>
              ))}
          </div>
        );
      })()}

      {selected && (() => {
        const st = statusFor(selected, selDate);
        const weekly = hours[String(st.py)] || null;
        return <DayEditor date={selected} override={st.ov} weekly={weekly} appts={apptsByDate[selected] || []} onClose={() => setSelected(null)} onSaved={load} />;
      })()}
      {newOpen && (
        <NewAppointmentDialog defaultDate={selected || format(new Date(), "yyyy-MM-dd")} onClose={() => setNewOpen(false)} onSaved={load} />
      )}
    </div>
  );
}

function NewAppointmentDialog({ defaultDate, onClose, onSaved }) {
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState("10:00");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [paid, setPaid] = useState(false);
  const [method, setMethod] = useState("cash");
  const [amountPaid, setAmountPaid] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/services").then((r) => {
      const flat = [];
      r.data.forEach((g) => g.items.forEach((it) => flat.push({ id: it.id, label: it.type === "Single" ? it.group : `${it.group} — ${it.type}`, price: it.price })));
      setServices(flat);
      if (flat.length) setServiceId(flat[0].id);
    });
  }, []);

  const valid = serviceId && date && time && name.trim() && /\S+@\S+\.\S+/.test(email) && phone.trim().length >= 6;

  const save = async () => {
    setSaving(true); setErr("");
    const svc = services.find((s) => s.id === serviceId);
    const price = svc ? svc.price : 0;
    const eff = paid ? (amountPaid === "" ? price : Math.max(0, Math.min(Number(amountPaid) || 0, price))) : 0;
    try {
      await api.post("/admin/bookings", { service_id: serviceId, date, time, client_name: name, client_email: email, client_phone: phone, notes, paid, payment_method: paid ? method : null, amount_paid: eff });
      await onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not create appointment.");
    } finally { setSaving(false); }
  };

  const _svc = services.find((s) => s.id === serviceId);
  const _price = _svc ? _svc.price : 0;
  const _eff = amountPaid === "" ? _price : Math.max(0, Math.min(Number(amountPaid) || 0, _price));
  const _balance = Math.max(0, _price - _eff);

  const inputCls = "w-full bg-[#0A0A0A] border border-[#333] px-3 py-2.5 text-sm rounded text-white focus:border-[#C5B382] outline-none";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="new-appointment-dialog" onClick={(e) => e.stopPropagation()} className="bg-[#111] border border-[#333] rounded-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl">New appointment</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1.5">Service</label>
          <select data-testid="new-service" value={serviceId} onChange={(e) => setServiceId(e.target.value)} className={inputCls}>
            {services.map((s) => <option key={s.id} value={s.id}>{s.label} — ${s.price.toFixed(0)}</option>)}
          </select>
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1.5">Date</label>
            <input data-testid="new-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </div>
          <div className="w-32">
            <label className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1.5">Time</label>
            <input data-testid="new-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
          </div>
        </div>
        <input data-testid="new-name" type="text" placeholder="Client name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
        <input data-testid="new-email" type="email" placeholder="Client email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
        <input data-testid="new-phone" type="tel" placeholder="Client phone" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
        <textarea data-testid="new-notes" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} />
        <div className="border border-[#222] bg-[#0E0E0E] rounded-xl p-3 space-y-2">
          <label className="flex items-center gap-3 cursor-pointer">
            <input data-testid="new-paid" type="checkbox" checked={paid}
              onChange={(e) => { setPaid(e.target.checked); if (e.target.checked && amountPaid === "") setAmountPaid(String(_price)); }} />
            <span className="text-sm text-neutral-300">Payment taken now (cash / transfer / card)</span>
          </label>
          {paid && (
            <div className="space-y-2">
              <select data-testid="new-method" value={method} onChange={(e) => setMethod(e.target.value)} className={inputCls}>
                <option value="cash">Cash</option>
                <option value="transfer">Bank transfer</option>
                <option value="card">Card in person</option>
              </select>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Amount paid (max ${_price.toFixed(0)})</label>
                <input data-testid="new-amount" type="number" min="0" step="1" max={_price} value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)} placeholder={`${_price}`} className={inputCls} />
              </div>
              <p data-testid="new-balance" className="text-xs text-neutral-400">
                Paid <span className="text-[#C5B382]">${_eff.toFixed(2)}</span> · Balance left to pay <span className={_balance > 0 ? "text-orange-400" : "text-green-400"}>${_balance.toFixed(2)}</span>
              </p>
            </div>
          )}
        </div>
        <p className="text-xs text-neutral-600">{paid ? (_balance > 0 ? `Deposit taken — $${_balance.toFixed(0)} balance will show as due in Bookings.` : "Marked as fully paid — no balance due.") : "Created as confirmed & unpaid — collect payment in person, or use the balance buttons in Bookings."} The client gets a confirmation email with a calendar invite.</p>
        {err && <p className="text-red-400 text-sm">{err}</p>}
        <div className="flex justify-end">
          <button data-testid="new-save" onClick={save} disabled={!valid || saving}
            className="bg-[#C5B382] hover:bg-[#B8A572] disabled:opacity-30 text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider px-6 py-2.5 rounded-full transition-colors">
            {saving ? "…" : "Create appointment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientsTab() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const search = () => api.get("/admin/clients", { params: { q } }).then((r) => setList(r.data));
  useEffect(() => { search(); }, []);
  const open = (email) => api.get(`/admin/clients/${encodeURIComponent(email)}`).then((r) => setSel(r.data));
  return (
    <div data-testid="admin-clients" className="grid md:grid-cols-3 gap-4">
      <div className="md:col-span-1 space-y-2">
        <div className="flex gap-2">
          <input data-testid="client-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search name, email, phone" className="flex-1 bg-[#0A0A0A] border border-[#333] px-3 py-2 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
          <button data-testid="client-search-btn" onClick={search} className="text-xs bg-[#C5B382] text-black px-3 rounded">Search</button>
        </div>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
          {list.map((c) => (
            <button key={c.email} data-testid={`client-item-${c.email}`} onClick={() => open(c.email)}
              className={`w-full text-left border rounded-lg px-3 py-2 transition-colors ${sel && sel.email === c.email ? "border-[#C5B382] bg-[#C5B382]/10" : "border-[#222] hover:border-[#444]"}`}>
              <div className="text-sm text-white">{c.name}</div>
              <div className="text-xs text-neutral-500">{c.visits} visit{c.visits > 1 ? "s" : ""} · {c.email}</div>
              {c.general_notes && <div className="text-[10px] text-orange-400 mt-0.5 truncate">⚠ {c.general_notes}</div>}
            </button>
          ))}
          {list.length === 0 && <p className="text-neutral-600 text-sm">No clients found.</p>}
        </div>
      </div>
      <div className="md:col-span-2">
        {sel ? <ClientProfile key={sel.email} profile={sel} onSaved={() => { open(sel.email); search(); }} /> : <p className="text-neutral-600 text-sm">Select a client to view their profile.</p>}
      </div>
    </div>
  );
}

function ClientProfile({ profile, onSaved }) {
  const [notes, setNotes] = useState(profile.general_notes || "");
  const [saving, setSaving] = useState(false);
  const saveNotes = async () => {
    setSaving(true);
    try { await api.put(`/admin/clients/${encodeURIComponent(profile.email)}`, { general_notes: notes }); await onSaved(); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-serif text-2xl">{profile.name}</h3>
        <p className="text-sm text-neutral-500">{profile.email} · {profile.phone}</p>
      </div>
      <div className="border border-orange-400/40 bg-orange-400/5 rounded-xl p-4">
        <label className="text-[10px] uppercase tracking-wider text-orange-300 block mb-1.5">⚠ Important notes (allergies, sensitivities)</label>
        <textarea data-testid="client-general-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
          className="w-full bg-[#0A0A0A] border border-[#333] px-3 py-2 text-sm rounded text-white focus:border-[#C5B382] outline-none" />
        <button data-testid="client-notes-save" onClick={saveNotes} disabled={saving} className="mt-2 text-xs bg-[#C5B382] text-black px-4 py-1.5 rounded-full">{saving ? "…" : "Save notes"}</button>
      </div>
      <div className="space-y-3">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">Appointments ({profile.bookings.length})</div>
        {profile.bookings.map((b) => <ApptNotes key={b.id} b={b} onSaved={onSaved} />)}
      </div>
    </div>
  );
}

function ApptNotes({ b, onSaved }) {
  const [adminN, setAdminN] = useState(b.admin_notes || "");
  const [lashN, setLashN] = useState(b.lash_notes || "");
  const [busy, setBusy] = useState(false);
  const inp = "w-full bg-[#0A0A0A] border border-[#333] px-3 py-2 text-sm rounded text-white focus:border-[#C5B382] outline-none";
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/bookings/${b.id}/notes`, { admin_notes: adminN, lash_notes: lashN }); await onSaved(); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-[#222] bg-[#111] rounded-xl p-4 space-y-2">
      <div className="flex justify-between text-sm"><span className="text-[#C5B382]">{b.date_display}</span><span className="text-neutral-400">{b.time} · {b.service_name}</span></div>
      <div><label className="text-[10px] uppercase tracking-wider text-neutral-500 block mb-1">Appointment notes</label><textarea data-testid={`admin-notes-${b.id}`} value={adminN} onChange={(e) => setAdminN(e.target.value)} rows={2} className={inp} /></div>
      <div><label className="text-[10px] uppercase tracking-wider text-[#C5B382] block mb-1">Lash extension notes</label><textarea data-testid={`lash-notes-${b.id}`} value={lashN} onChange={(e) => setLashN(e.target.value)} rows={2} className={inp} placeholder="Curl, length, thickness, mapping, glue, retention…" /></div>
      <button data-testid={`notes-save-${b.id}`} onClick={save} disabled={busy} className="text-xs bg-[#C5B382] text-black px-4 py-1.5 rounded-full">{busy ? "…" : "Save"}</button>
    </div>
  );
}

function PaymentsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/payments").then((r) => setData(r.data)); }, []);
  if (!data) return <Loader2 className="animate-spin text-[#C5B382]" />;
  const label = { cash: "Cash", transfer: "Transfer", card: "Card", online: "Online (Stripe)", other: "Other" };
  return (
    <div data-testid="admin-payments" className="space-y-4">
      <div className="border border-[#C5B382]/25 bg-[#C5B382]/5 rounded-xl px-4 py-3 text-sm text-neutral-300">
        Total collected: <span data-testid="payments-grand-total" className="text-[#C5B382] font-semibold">${data.grand_total.toFixed(2)}</span>
      </div>
      {data.days.length === 0 ? (
        <p className="text-neutral-500">No payments recorded yet. Deposits, in-person payments and online payments will appear here per day.</p>
      ) : data.days.map((d) => (
        <div key={d.date} data-testid={`payments-day-${d.date}`} className="border border-[#222] bg-[#111] rounded-xl p-4">
          <div className="flex items-center justify-between">
            <span className="text-[#C5B382] font-medium">{d.date_display}</span>
            <span className="font-semibold">${d.total.toFixed(2)}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(d.methods).map(([m, v]) => (
              <span key={m} className="text-xs text-neutral-400 border border-[#333] rounded-full px-2.5 py-1">{label[m] || m}: ${v.toFixed(2)}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function WaitlistTab() {
  const [entries, setEntries] = useState(null);
  const load = () => api.get("/admin/waitlist").then((r) => setEntries(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/admin/waitlist/${id}`);
    load();
  };

  if (!entries) return <Loader2 className="animate-spin text-[#C5B382]" />;
  if (!entries.length)
    return <p className="text-neutral-500">No waitlist requests yet. When a day is fully booked, clients can join the waitlist and they'll appear here.</p>;

  return (
    <div data-testid="admin-waitlist" className="space-y-3">
      {entries.map((e) => (
        <div key={e.id} className="border border-[#222] bg-[#111] p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium"><span className="text-[#C5B382]">{e.preferred_date_display}</span> · {e.service_name}</div>
            <div className="text-sm text-neutral-500">{e.name} · {e.email} · {e.phone}</div>
          </div>
          <div className="flex items-center gap-3">
            <a href={`mailto:${e.email}`} className="text-xs text-[#C5B382] hover:text-white transition-colors">Email</a>
            <button
              data-testid={`waitlist-remove-${e.id}`}
              onClick={() => remove(e.id)}
              className="text-xs text-neutral-500 hover:text-red-400 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ServicesTab() {
  const [services, setServices] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/admin/services").then((r) => setServices(r.data));
  useEffect(() => { load(); }, []);

  const update = (id, field, value) =>
    setServices((s) => s.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  const save = async (svc) => {
    setSaving(svc.id);
    await api.put(`/admin/services/${svc.id}`, {
      price: parseFloat(svc.price),
      duration: parseInt(svc.duration),
      description: svc.description,
      active: svc.active,
    });
    setSaving(null);
  };

  if (!services) return <Loader2 className="animate-spin text-[#C5B382]" />;

  return (
    <div data-testid="admin-services" className="space-y-3">
      {services.map((s) => (
        <div key={s.id} className="border border-[#222] bg-[#111] p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-[180px] font-medium">{s.group} <span className="text-neutral-500 text-sm">· {s.type}</span></div>
            <label className="text-xs text-neutral-500 flex items-center gap-2">
              $<input
                data-testid={`svc-price-${s.id}`}
                type="number" value={s.price}
                onChange={(e) => update(s.id, "price", e.target.value)}
                className="w-20 bg-[#0A0A0A] border border-[#333] px-2 py-1.5 text-white text-sm focus:border-[#C5B382] outline-none"
              />
            </label>
            <label className="text-xs text-neutral-500 flex items-center gap-2">
              <input
                data-testid={`svc-duration-${s.id}`}
                type="number" value={s.duration}
                onChange={(e) => update(s.id, "duration", e.target.value)}
                className="w-20 bg-[#0A0A0A] border border-[#333] px-2 py-1.5 text-white text-sm focus:border-[#C5B382] outline-none"
              /> min
            </label>
            <label className="text-xs text-neutral-400 flex items-center gap-2 ml-auto">
              <input type="checkbox" checked={s.active} onChange={(e) => update(s.id, "active", e.target.checked)} /> Active
            </label>
            <button
              data-testid={`svc-save-${s.id}`}
              onClick={() => save(s)}
              className="bg-[#C5B382] hover:bg-[#B8A572] text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider px-4 py-1.5 transition-colors"
            >
              {saving === s.id ? "…" : "Save"}
            </button>
          </div>
          <input
            value={s.description}
            onChange={(e) => update(s.id, "description", e.target.value)}
            className="w-full mt-3 bg-[#0A0A0A] border border-[#333] px-3 py-2 text-white text-sm focus:border-[#C5B382] outline-none"
          />
        </div>
      ))}
    </div>
  );
}

const NOTICE_LABELS = { "3_hours": "3 hours", "6_hours": "6 hours", "12_hours": "12 hours", "1_day": "1 day", "3_days": "3 days", "1_week": "1 week" };
const ADVANCE_LABELS = { "1_week": "1 week", "2_weeks": "2 weeks", "1_month": "1 month", "2_months": "2 months", "3_months": "3 months", "6_months": "6 months", "12_months": "12 months" };

function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const bookLink = `${window.location.origin}/book`;
  const copyLink = () => {
    navigator.clipboard.writeText(bookLink);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)); }, []);

  const updateDay = (idx, field, value) =>
    setSettings((s) => ({ ...s, hours: { ...s.hours, [idx]: { ...s.hours[idx], [field]: value } } }));

  const save = async () => {
    await api.put("/admin/settings", {
      hours: settings.hours,
      slot_interval: parseInt(settings.slot_interval),
      studio_full_address: settings.studio_full_address,
      blocked_dates: settings.blocked_dates || [],
      waitlist_alerts: !!settings.waitlist_alerts,
      minimum_booking_notice: settings.minimum_booking_notice || "12_hours",
      maximum_booking_advance: settings.maximum_booking_advance || "3_months",
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  if (!settings) return <Loader2 className="animate-spin text-[#C5B382]" />;

  return (
    <div data-testid="admin-settings" className="max-w-2xl space-y-6">
      <div>
        <div className="eyebrow text-[#C5B382] mb-3">Shareable booking link</div>
        <p className="text-xs text-neutral-500 mb-3">Post this link on Instagram or anywhere — it opens the booking window straight away.</p>
        <div className="flex flex-wrap items-center gap-2">
          <code data-testid="book-link" className="flex-1 min-w-[200px] bg-[#0A0A0A] border border-[#333] rounded-lg px-3 py-2.5 text-sm text-[#C5B382] truncate">{bookLink}</code>
          <button data-testid="copy-book-link" onClick={copyLink}
            className="bg-[#C5B382] hover:bg-[#B8A572] text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider px-5 py-2.5 rounded-full transition-colors">
            {copied ? "Copied ✓" : "Copy link"}
          </button>
        </div>
      </div>

      <div>
        <div className="eyebrow text-[#C5B382] mb-4">Opening Hours</div>
        <div className="space-y-2">
          {DAYS.map((day, idx) => {
            const h = settings.hours[String(idx)];
            return (
              <div key={idx} className="flex items-center gap-3 border border-[#222] bg-[#111] p-3">
                <span className="w-28 text-sm">{day}</span>
                <label className="text-xs text-neutral-400 flex items-center gap-1.5">
                  <input
                    data-testid={`hours-closed-${idx}`}
                    type="checkbox" checked={!h.closed}
                    onChange={(e) => updateDay(String(idx), "closed", !e.target.checked)}
                  /> Open
                </label>
                {!h.closed && (
                  <>
                    <input type="time" value={h.open} onChange={(e) => updateDay(String(idx), "open", e.target.value)}
                      className="bg-[#0A0A0A] border border-[#333] px-2 py-1 text-sm text-white focus:border-[#C5B382] outline-none" />
                    <span className="text-neutral-600">–</span>
                    <input type="time" value={h.close} onChange={(e) => updateDay(String(idx), "close", e.target.value)}
                      className="bg-[#0A0A0A] border border-[#333] px-2 py-1 text-sm text-white focus:border-[#C5B382] outline-none" />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="eyebrow text-[#C5B382] mb-3">Slot interval (minutes)</div>
        <input
          data-testid="slot-interval"
          type="number" value={settings.slot_interval}
          onChange={(e) => setSettings({ ...settings, slot_interval: e.target.value })}
          className="w-28 bg-[#111] border border-[#333] px-3 py-2 text-white text-sm focus:border-[#C5B382] outline-none"
        />
      </div>

      <div>
        <div className="eyebrow text-[#C5B382] mb-3">Studio address (sent in confirmation email)</div>
        <textarea
          data-testid="studio-address"
          rows={3} value={settings.studio_full_address}
          onChange={(e) => setSettings({ ...settings, studio_full_address: e.target.value })}
          className="w-full bg-[#111] border border-[#333] px-3 py-2 text-white text-sm focus:border-[#C5B382] outline-none resize-none"
        />
      </div>

      <div>
        <div className="eyebrow text-[#C5B382] mb-1">Client Booking Window</div>
        <p className="text-xs text-neutral-500 mb-4">Control how far in advance clients can book appointments.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-neutral-300 block mb-1.5">Minimum notice required before booking</label>
            <select data-testid="min-notice-select" value={settings.minimum_booking_notice || "12_hours"}
              onChange={(e) => setSettings({ ...settings, minimum_booking_notice: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#333] px-3 py-2.5 text-sm rounded text-white focus:border-[#C5B382] outline-none">
              <option value="3_hours">3 hours</option>
              <option value="6_hours">6 hours</option>
              <option value="12_hours">12 hours</option>
              <option value="1_day">1 day</option>
              <option value="3_days">3 days</option>
              <option value="1_week">1 week</option>
            </select>
            <p className="text-xs text-neutral-600 mt-1.5">Choose how much notice a client must give before booking.</p>
          </div>
          <div>
            <label className="text-sm text-neutral-300 block mb-1.5">How far in advance can clients book?</label>
            <select data-testid="max-advance-select" value={settings.maximum_booking_advance || "3_months"}
              onChange={(e) => setSettings({ ...settings, maximum_booking_advance: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#333] px-3 py-2.5 text-sm rounded text-white focus:border-[#C5B382] outline-none">
              <option value="1_week">1 week</option>
              <option value="2_weeks">2 weeks</option>
              <option value="1_month">1 month</option>
              <option value="2_months">2 months</option>
              <option value="3_months">3 months</option>
              <option value="6_months">6 months</option>
              <option value="12_months">12 months</option>
            </select>
            <p className="text-xs text-neutral-600 mt-1.5">The furthest ahead a client can make an appointment.</p>
          </div>
        </div>
        <div data-testid="booking-window-summary" className="mt-4 border border-[#C5B382]/25 bg-[#C5B382]/5 rounded-xl px-4 py-3 text-sm text-neutral-300">
          Clients can currently book appointments from <span className="text-[#C5B382] font-medium">{NOTICE_LABELS[settings.minimum_booking_notice || "12_hours"]}</span> from now up to <span className="text-[#C5B382] font-medium">{ADVANCE_LABELS[settings.maximum_booking_advance || "3_months"]}</span> in advance.
        </div>
      </div>

      <div>
        <div className="eyebrow text-[#C5B382] mb-3">Waitlist alerts</div>
        <label className="flex items-start gap-3 border border-[#222] bg-[#111] p-4 cursor-pointer">
          <input
            data-testid="waitlist-alerts-toggle"
            type="checkbox"
            checked={!!settings.waitlist_alerts}
            onChange={(e) => setSettings({ ...settings, waitlist_alerts: e.target.checked })}
            className="mt-0.5"
          />
          <span className="text-sm text-neutral-300 leading-relaxed">
            Automatically email waitlisted clients the moment a spot opens up on their preferred day. When off, waitlist requests only appear in the Waitlist tab for you to contact manually.
          </span>
        </label>
      </div>

      <button
        data-testid="save-settings"
        onClick={save}
        className="bg-[#C5B382] hover:bg-[#B8A572] text-[#0A0A0A] text-xs font-semibold uppercase tracking-[0.18em] px-8 py-3 transition-colors"
      >
        {saved ? "Saved ✓" : "Save Settings"}
      </button>
    </div>
  );
}
